import { registerTranslationRefresh, t as lnT } from '../../../../i18n';
import {useTranslation} from 'react-i18next';
import {SPOKEN_LANGUAGES, getDefaultSpokenLanguage} from '../../../../i18n/spokenLanguages';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ReactPlayer from 'react-player';
import Duration from '../../../Duration';
import { IoIosPause, IoIosRepeat, IoIosVolumeHigh, IoIosVolumeOff } from 'react-icons/io';
import { FaPlay } from 'react-icons/fa';
import { FiArrowRight, FiClock, FiFileText, FiSettings } from 'react-icons/fi';
import { LuScanText } from 'react-icons/lu';

import '../../../AudioControls.scss';
import './AudioPlayer.scss';

import { WHITE, PALE_WHITE_1 } from '../../../../appearance/Colors';

import { useMutation, useQuery } from '@apollo/client';
import { MUTATION, QUERY } from '../../../../gql-operations';
import { GET_FEATURE_FLAG } from '../../../../gql-operations/featureFlags';
import CreditsRequiredModal from '../../../Modals/CreditsRequiredModal';
import Modal from '../../../Modals/Modal';

import useCredits from '../../../../hooks/useCredits';
import { openSettingsModal, SETTINGS_MODAL_TAB } from '../../../../util/settingsModalEvents';
import {
    FEATURE_UPGRADE_MODAL_PRESET,
    openFeatureUpgradeModal
} from '../../../../util/featureUpgradeModalEvents';
import {
    evaluateFeatureFlagForUser,
    getFeatureFlagUpgradeMessage,
    mapTranscriptionErrorToUiAction
} from '../../../../util/featureFlagAccess';
import { normalizeDurationToSeconds, resolvePreferredMediaDuration } from '../../../../util/mediaDuration';
import {
    AUDIO_TRANSCRIPTION_OUTPUT_FORMAT,
    AUDIO_TRANSCRIPTION_VISIBLE_OPTIONS
} from '../../../../util/audioTranscriptionOptions';
import {
    getApolloErrorMessage,
    getAudioProcessingStatusText,
    getAudioProcessingStatusTone,
    isAudioProcessingActive
} from '../../../../util/audioProcessingStatus';
import { setOpenMenuMessage, getOpenMenuMessage, addMenuListener } from '../../../Messages/DefaultMessage';
import {
    DropDownMenu,
    Element,
    MenuItemLabel,
    MenuItemIcon
} from '../../../Messages/MessageContextMenu';

const PLAYBACK_SPEEDS = [1, 1.5, 2, 2.5];
const ADVANCED_TRANSCRIPTION_OPTION_VALUE = 'ADVANCED';
const makeTranslatedPREVIEW_AUDIO_TRANSCRIPTION_MENU_OPTIONS = () => ([
    ...AUDIO_TRANSCRIPTION_VISIBLE_OPTIONS,
    {
        value: ADVANCED_TRANSCRIPTION_OPTION_VALUE,
        menuLabel: lnT('messages:advanced'),
        label: lnT('messages:advanced'),
        description: lnT('messages:chooseTranscriptionProviderAndSourceLanguage')
    }
]);
let PREVIEW_AUDIO_TRANSCRIPTION_MENU_OPTIONS = makeTranslatedPREVIEW_AUDIO_TRANSCRIPTION_MENU_OPTIONS();
registerTranslationRefresh(() => { PREVIEW_AUDIO_TRANSCRIPTION_MENU_OPTIONS = makeTranslatedPREVIEW_AUDIO_TRANSCRIPTION_MENU_OPTIONS(); });
const SOURCE_LANGUAGE_OPTIONS = SPOKEN_LANGUAGES;

const transcriptionModalStyles = {
    overlay: {
        zIndex: 1006
    },
    content: {
        position: 'absolute',
        inset: '50% auto auto 50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(680px, calc(100vw - 32px))',
        maxHeight: 'min(760px, calc(100vh - 32px))',
        padding: 0,
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 16,
        background: '#12161d',
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.45)'
    }
};

const formatPlaybackRate = value => `${value}x`;

function AudioPlayer({ url, fallbackUrl, fileName, title, style, message, initialDuration, attachmentActions, readOnly = false, canGenerate = true }) {
    const {t: lnT} = useTranslation();

    const playerRef = useRef(null);
    const isResettingAfterEndRef = useRef(false);
    const durationSyncTimeoutRef = useRef(null);
    const hasRetriedPlaybackUrlRef = useRef(false);

    const [playing, setPlaying] = useState(false);
    const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
    const [muted, setMuted] = useState(false);
    const [loop, setLoop] = useState(false);
    const [played, setPlayed] = useState(0);
    const normalizedInitialDuration = normalizeDurationToSeconds(initialDuration, { unitHint: lnT('common:seconds') });
    const [duration, setDuration] = useState(normalizedInitialDuration ?? 0);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const [seeking, setSeeking] = useState(false);
    const [playbackUrl, setPlaybackUrl] = useState(url);
    const [isRefreshingPlaybackUrl, setIsRefreshingPlaybackUrl] = useState(false);

    // Advanced transcription state
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcriptionError, setTranscriptionError] = useState(null);
    const [transcriptionStatus, setTranscriptionStatus] = useState(null);
    const [showTranscriptionOutputMenu, setShowTranscriptionOutputMenu] = useState(false);
    const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const languageTouched = useRef(false);
    const [showLanguagePicker, setShowLanguagePicker] = useState(false);
    const [languageSearchQuery, setLanguageSearchQuery] = useState('');
    const [selectedProvider, setSelectedProvider] = useState('openai'); // 'openai' or 'groq'
    const [selectedGroqModel, setSelectedGroqModel] = useState('whisper_large_v3_turbo'); // 'whisper_large_v3' or 'whisper_large_v3_turbo'
    const [showCreditsRequiredModal, setShowCreditsRequiredModal] = useState(false);
    const [transcriptionMenuPosition, setTranscriptionMenuPosition] = useState(null);
    const transcriptionMenuTriggerRef = useRef(null);
    const transcriptionMenuRef = useRef(null);
    const transcriptionMenuKey = React.useMemo(
        () => `audio-preview-transcribe:${message?.id || fileName || url}`,
        [lnT, fileName, message?.id, url]
    );

    const syncDurationFromInternalPlayer = React.useCallback(() => {
        const internalPlayer = playerRef.current?.getInternalPlayer?.();
        const nextDuration = resolvePreferredMediaDuration({
            storedDuration: normalizedInitialDuration,
            runtimeDuration: internalPlayer?.duration
        });

        if (nextDuration !== null) {
            setDuration(nextDuration);
            return true;
        }

        return false;
    }, [lnT, normalizedInitialDuration]);

    // Get user data for feature flag evaluation
    const { data: userData } = useQuery(QUERY.ME, {
        errorPolicy: 'all',
        skip: readOnly
    });
    const { refetch: refetchDownloadFile } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });
    
    // Feature flag check for audio transcription
    const { data: transcriptionFlagData, loading: transcriptionFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: 'audio_transcription' },
        errorPolicy: 'all',
        skip: readOnly
    });
    
    const transcriptionFlag = transcriptionFlagData?.getFeatureFlag;
    const transcriptionAccessDecision = React.useMemo(
        () => evaluateFeatureFlagForUser(transcriptionFlag, userData?.me, { loading: transcriptionFlagLoading }),
        [lnT, transcriptionFlag, userData?.me, transcriptionFlagLoading]
    );
    const showTranscriptionAction = !readOnly && canGenerate && !!transcriptionFlag?.enabled;
    const requiresUpgradeForTranscription = Boolean(
        showTranscriptionAction && transcriptionAccessDecision?.requiresUpgrade
    );
    const requiresTeamUpgradeForTranscription = Boolean(
        showTranscriptionAction && transcriptionAccessDecision?.requiresTeamUpgrade
    );
    const upgradeTooltip = getFeatureFlagUpgradeMessage(transcriptionFlag, 'audio transcription');
    const { credits } = useCredits({
        skip: readOnly || !userData?.me?.id,
        fallbackCredits: userData?.me?.quota?.credits
    });
    useEffect(() => {
        if (!languageTouched.current) setSelectedLanguage(getDefaultSpokenLanguage(userData?.me));
    }, [userData?.me?.defaultTranscriptionLanguage, userData?.me?.spokenLanguages, userData?.me?.spokenLanguagesConfigured]);
    const filteredLanguageOptions = React.useMemo(() => {
        const normalizedQuery = String(languageSearchQuery || '').trim().toLowerCase();

        const preferred = userData?.me?.spokenLanguages || [];
        return SOURCE_LANGUAGE_OPTIONS.filter(option =>
            !normalizedQuery || `${option.label} ${option.value}`.toLowerCase().includes(normalizedQuery)
        ).sort((a, b) => Number(preferred.includes(b.value)) - Number(preferred.includes(a.value)));
    }, [lnT, languageSearchQuery, userData?.me?.spokenLanguages]);
    const selectedLanguageOption = React.useMemo(
        () => SOURCE_LANGUAGE_OPTIONS.find(option => option.value === selectedLanguage) || null,
        [lnT, selectedLanguage]
    );
    const audioProcessing = message?.metadata?.audioProcessing;
    const serverTranscriptionStatus = getAudioProcessingStatusText(audioProcessing);
    const transcriptionStatusTone = getAudioProcessingStatusTone(audioProcessing);

    // Transcription mutation
    const [transcribeAudio] = useMutation(MUTATION.TRANSCRIBE_AUDIO, {
        onCompleted: (data) => {
            setIsTranscribing(false);
            if (data.transcribeAudio.__typename === 'TranscribeAudioError') {
                const errorMessage = data.transcribeAudio.message || lnT('messages:failedToTranscribeAudioPleaseTryAgain');
                const uiAction = mapTranscriptionErrorToUiAction({
                    error: data.transcribeAudio,
                    defaultDecision: transcriptionAccessDecision
                });
                if (uiAction.openUpgradeModal) {
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                        upgradeType: 'personal'
                    });
                    setTranscriptionError(null);
                    return;
                }
                if (uiAction.openTeamUpgradeModal) {
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                        upgradeType: 'team'
                    });
                    setTranscriptionError(null);
                    return;
                }
                if (data.transcribeAudio.code === 'INSUFFICIENT_CREDITS') {
                    setShowCreditsRequiredModal(true);
                    setTranscriptionError(null);
                    return;
                }
                setTranscriptionError(uiAction.showMessage || errorMessage);
            } else {
                const queuedStatus = getAudioProcessingStatusText(data.transcribeAudio?.metadata?.audioProcessing);
                setTranscriptionStatus(
                    queuedStatus || lnT('messages:transcriptionRunningInTheBackground')
                );
                setTranscriptionError(null);
                // The message will be updated via subscription, so no need to manually update
            }
        },
        onError: (error) => {
            setIsTranscribing(false);
            const errorMessage = getApolloErrorMessage(error, 'Failed to transcribe audio. Please try again.');
            if (errorMessage?.includes('Insufficient Credits')) {
                setShowCreditsRequiredModal(true);
                setTranscriptionError(null);
                return;
            }
            setTranscriptionError(errorMessage);
            console.error('Transcription error:', error);
        }
    });

    useEffect(() => {
        if (!isAudioProcessingActive(audioProcessing)) {
            setTranscriptionStatus(null);
        }
    }, [audioProcessing?.status, message?.body]);

    useEffect(() => {
        return () => {
            handleStop();
        };
    }, [url]);

    useEffect(() => {
        setPlaybackUrl(url);
        hasRetriedPlaybackUrlRef.current = false;
        isResettingAfterEndRef.current = false;
        setShowTranscriptionOutputMenu(false);
        setTranscriptionMenuPosition(null);
        if (durationSyncTimeoutRef.current) {
            window.clearTimeout(durationSyncTimeoutRef.current);
            durationSyncTimeoutRef.current = null;
        }
    }, [url, fileName]);

    useEffect(() => {
        setDuration(normalizedInitialDuration ?? 0);
    }, [normalizedInitialDuration, url, fileName]);

    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const internalPlayer = playerRef.current?.getInternalPlayer?.();

        const scheduleRetry = () => {
            if (cancelled || attempts >= 12) {
                return;
            }
            attempts += 1;
            durationSyncTimeoutRef.current = window.setTimeout(() => {
                if (!cancelled && !syncDurationFromInternalPlayer()) {
                    scheduleRetry();
                }
            }, 250);
        };

        const handleMetadata = () => {
            syncDurationFromInternalPlayer();
        };

        if (internalPlayer?.addEventListener) {
            internalPlayer.addEventListener('loadedmetadata', handleMetadata);
            internalPlayer.addEventListener('durationchange', handleMetadata);
            internalPlayer.addEventListener('canplay', handleMetadata);
        }

        if (!syncDurationFromInternalPlayer() && normalizedInitialDuration === null) {
            scheduleRetry();
        }

        return () => {
            cancelled = true;
            if (durationSyncTimeoutRef.current) {
                window.clearTimeout(durationSyncTimeoutRef.current);
                durationSyncTimeoutRef.current = null;
            }
            if (internalPlayer?.removeEventListener) {
                internalPlayer.removeEventListener('loadedmetadata', handleMetadata);
                internalPlayer.removeEventListener('durationchange', handleMetadata);
                internalPlayer.removeEventListener('canplay', handleMetadata);
            }
        };
    }, [normalizedInitialDuration, playbackUrl, syncDurationFromInternalPlayer]);

    useEffect(() => {
        if (!showTranscriptionModal) {
            setShowLanguagePicker(false);
            setLanguageSearchQuery('');
        }
    }, [showTranscriptionModal]);

    useEffect(() => {
        const listener = (previousId, currentId) => {
            if (currentId === transcriptionMenuKey) {
                setShowTranscriptionOutputMenu(true);
            } else if (previousId === transcriptionMenuKey) {
                setShowTranscriptionOutputMenu(false);
            }
        };

        const removeListener = addMenuListener(listener);
        if (getOpenMenuMessage() === transcriptionMenuKey) {
            setShowTranscriptionOutputMenu(true);
        }

        return removeListener;
    }, [transcriptionMenuKey]);

    useEffect(() => {
        if (!showTranscriptionOutputMenu) {
            return undefined;
        }

        const handleOutsideClick = event => {
            if (transcriptionMenuRef.current?.contains(event.target)) {
                return;
            }
            if (transcriptionMenuTriggerRef.current?.contains(event.target)) {
                return;
            }
            setOpenMenuMessage(null);
        };

        const handleEscape = event => {
            if (event.key === 'Escape') {
                setOpenMenuMessage(null);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        document.addEventListener('keydown', handleEscape, true);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape, true);
        };
    }, [showTranscriptionOutputMenu]);

    const updateTranscriptionMenuPosition = React.useCallback(() => {
        if (!transcriptionMenuTriggerRef.current || typeof window === 'undefined') {
            return;
        }

        const rect = transcriptionMenuTriggerRef.current.getBoundingClientRect();
        const estimatedMenuHeight = 182;
        const openUp = rect.bottom + estimatedMenuHeight > window.innerHeight - 8;

        setTranscriptionMenuPosition({
            top: openUp ? Math.max(8, rect.top - 6) : rect.bottom + 6,
            right: Math.max(8, window.innerWidth - rect.right),
            openUp
        });
    }, [lnT]);

    useEffect(() => {
        if (!showTranscriptionOutputMenu) {
            return undefined;
        }

        updateTranscriptionMenuPosition();
        window.addEventListener('resize', updateTranscriptionMenuPosition);
        window.addEventListener('scroll', updateTranscriptionMenuPosition, true);

        return () => {
            window.removeEventListener('resize', updateTranscriptionMenuPosition);
            window.removeEventListener('scroll', updateTranscriptionMenuPosition, true);
        };
    }, [showTranscriptionOutputMenu, updateTranscriptionMenuPosition]);

    const handlePlayPause = () => {
        if (!playing) {
            isResettingAfterEndRef.current = false;
        }
        setPlaying(!playing);
    };

    const resetPlayer = () => {
        if (playerRef.current) {
            playerRef.current.seekTo(0);
        }
    };

    const handleStop = () => {
        isResettingAfterEndRef.current = true;
        resetPlayer();
        setPlaying(false);
        setPlayed(0);
        setHasStartedPlayback(false);
        setSeeking(false);
    };

    const handleTranscribe = (options = {}) => {
        if (readOnly || !canGenerate) return;
        if (requiresUpgradeForTranscription) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                upgradeType: 'personal'
            });
            return;
        }
        if (requiresTeamUpgradeForTranscription) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                upgradeType: 'team'
            });
            return;
        }

        if (!message || !message.id) {
            setTranscriptionError(lnT('messages:messageIdNotAvailable'));
            return;
        }

        if (credits <= 0) {
            setShowCreditsRequiredModal(true);
            return;
        }
        
        setIsTranscribing(true);
        setTranscriptionError(null);
        setTranscriptionStatus(null);
        setShowTranscriptionModal(false);
        setOpenMenuMessage(null);
        
        const transcriptionData = {
            messageId: message.id,
            provider: selectedProvider,
            ...(selectedProvider === 'groq' && { groqModel: selectedGroqModel }),
            ...options
        };
        
        transcribeAudio({
            variables: {
                data: transcriptionData
            }
        });
    };

    const handleOpenTranscriptionModal = () => {
        if (isTranscribing) {
            return;
        }

        if (requiresUpgradeForTranscription || requiresTeamUpgradeForTranscription) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                upgradeType: requiresTeamUpgradeForTranscription ? 'team' : 'personal'
            });
            return;
        }

        setTranscriptionError(null);
        setOpenMenuMessage(null);
        setShowTranscriptionModal(true);
    };

    const handleOpenTranscriptionOutputMenu = () => {
        if (isTranscribing) {
            return;
        }

        if (requiresUpgradeForTranscription || requiresTeamUpgradeForTranscription) {
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                upgradeType: requiresTeamUpgradeForTranscription ? 'team' : 'personal'
            });
            return;
        }

        setTranscriptionError(null);
        if (showTranscriptionOutputMenu) {
            setOpenMenuMessage(null);
            return;
        }

        const currentlyOpen = getOpenMenuMessage();
        if (currentlyOpen && currentlyOpen !== transcriptionMenuKey) {
            setOpenMenuMessage(null);
        }

        setOpenMenuMessage(transcriptionMenuKey);
    };

    const handleSelectTranscriptionOutput = outputFormat => {
        if (outputFormat === ADVANCED_TRANSCRIPTION_OPTION_VALUE) {
            handleOpenTranscriptionModal();
            return;
        }

        handleTranscribe({
            outputFormat,
            provider:
                outputFormat === AUDIO_TRANSCRIPTION_OUTPUT_FORMAT.TIMESTAMPED
                    ? 'openai'
                    : selectedProvider,
            ...(outputFormat === AUDIO_TRANSCRIPTION_OUTPUT_FORMAT.TIMESTAMPED
                ? {}
                : (selectedProvider === 'groq' ? { groqModel: selectedGroqModel } : {})),
            language: selectedLanguage
        });
    };

    const handleCloseTranscriptionModal = () => {
        if (!isTranscribing) {
            setShowTranscriptionModal(false);
        }
    };

    const renderTranscriptionMenuIcon = optionValue => {
        if (optionValue === ADVANCED_TRANSCRIPTION_OPTION_VALUE) {
            return <FiSettings size={14} />;
        }

        if (optionValue === AUDIO_TRANSCRIPTION_OUTPUT_FORMAT.TIMESTAMPED) {
            return <FiClock size={14} />;
        }

        if (optionValue === AUDIO_TRANSCRIPTION_OUTPUT_FORMAT.SUMMARIZED) {
            return <LuScanText size={14} />;
        }

        return <FiFileText size={14} />;
    };

    const renderTranscriptionMenuPortal = () => {
        if (!showTranscriptionOutputMenu || !transcriptionMenuPosition || typeof document === 'undefined') {
            return null;
        }

        return ReactDOM.createPortal(
            <DropDownMenu
                ref={node => {
                    transcriptionMenuRef.current = node;
                }}
                style={{
                    position: 'fixed',
                    top: transcriptionMenuPosition.top,
                    right: transcriptionMenuPosition.right,
                    transform: transcriptionMenuPosition.openUp ? 'translateY(-100%)' : 'none'
                }}
                onClick={e => e.stopPropagation()}
            >
                <div role="menu" style={{ width: 220 }}>
                    <div role="content">
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {PREVIEW_AUDIO_TRANSCRIPTION_MENU_OPTIONS.map(option => (
                                <li key={option.value}>
                                    <Element
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleSelectTranscriptionOutput(option.value);
                                        }}
                                    >
                                        <MenuItemLabel>
                                            <MenuItemIcon>
                                                {renderTranscriptionMenuIcon(option.value)}
                                            </MenuItemIcon>
                                            <span style={{ fontSize: 13, lineHeight: 1.2 }}>
                                                {option.menuLabel || option.label}
                                            </span>
                                        </MenuItemLabel>
                                    </Element>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </DropDownMenu>,
            document.body
        );
    };

    const handleSubmitTranscription = () => {
        const options = {};

        options.language = selectedLanguage;

        handleTranscribe(options);
    };

    //   const handleToggleControls = () => {
    //     const url = this.state.url
    //     this.setState({
    //       controls: !this.state.controls,
    //       url: null
    //     }, () => this.load(url))
    //   }

    const handleCyclePlaybackRate = e => {
        e.stopPropagation();
        const currentIndex = PLAYBACK_SPEEDS.findIndex(speed => speed === playbackRate);
        const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % PLAYBACK_SPEEDS.length : 0;
        setPlaybackRate(PLAYBACK_SPEEDS[nextIndex]);
    };

    const handleToggleMuted = e => {
        e.stopPropagation();
        setMuted(previous => !previous);
    };

    const handleToggleLoop = e => {
        e.stopPropagation();
        setLoop(previous => !previous);
    };

    const handlePlay = () => {
        console.log('onPlay');
        isResettingAfterEndRef.current = false;
        setPlaying(true);
        setHasStartedPlayback(true);
    };

    const handlePause = () => {
        console.log('onPause');
        setPlaying(false);
    };

    const handleSeekMouseDown = e => {
        e.stopPropagation();
        setSeeking(true);
    };

    const handleSeekChange = e => {
        e.stopPropagation();
        setPlayed(parseFloat(e.target.value));
    };

    const handleSeekMouseUp = e => {
        e.stopPropagation();
        setSeeking(false);
        if (playerRef.current) {
            playerRef.current.seekTo(parseFloat(e.target.value));
        }
    };

    const handleProgress = state => {
        if (isResettingAfterEndRef.current) {
            return;
        }
        // We only want to update time slider if we are not currently seeking
        if (!hasStartedPlayback && state.played > 0) {
            setHasStartedPlayback(true);
        }
        if (!seeking) {
            setPlayed(state.played);
        }
    };

    const handleEnded = () => {
        console.log('onEnded');
        isResettingAfterEndRef.current = true;
        if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
            playerRef.current.seekTo(0);
        }
        setPlaying(false);
        setPlayed(0);
        setHasStartedPlayback(false);
        setSeeking(false);
    };

    const handleDuration = duration => {
        const preferredDuration = resolvePreferredMediaDuration({
            storedDuration: normalizedInitialDuration,
            runtimeDuration: duration
        });
        if (preferredDuration !== null) {
            setDuration(preferredDuration);
        }
    };

    const refreshPlaybackUrl = async () => {
        if (readOnly) return null;
        if (!fileName || isRefreshingPlaybackUrl) {
            return false;
        }

        setIsRefreshingPlaybackUrl(true);
        try {
            const { data, error } = await refetchDownloadFile({
                data: {
                    fileName
                }
            });

            if (error) {
                return false;
            }

            if (data?.downloadFile?.__typename === 'S3File' && data?.downloadFile?.url) {
                setPlaybackUrl(data.downloadFile.url);
                return true;
            }
            return false;
        } catch (refreshError) {
            return false;
        } finally {
            setIsRefreshingPlaybackUrl(false);
        }
    };

    const handlePlaybackError = async error => {
        if (fallbackUrl && playbackUrl !== fallbackUrl) {
            setPlaybackUrl(fallbackUrl);
            return;
        }

        if (!hasRetriedPlaybackUrlRef.current) {
            hasRetriedPlaybackUrlRef.current = true;
            const refreshed = await refreshPlaybackUrl();
            if (refreshed) {
                return;
            }
        }

        setPlaying(false);
        console.error('Audio playback failed', {
            messageId: message?.id,
            fileName,
            error: error?.message || error
        });
    };

    //   const handleClickFullscreen = () => {
    //     screenfull.request(findDOMNode(this.player))
    //   }

    // TODO Error on Yandex and strange behaviour on Firefox
    // desired solution convert the file on the server to ogg/mp3
    // https://stackoverflow.com/questions/37674223/domexception-failed-to-load-because-no-supported-source-was-found
    // var playPromise = document.querySelector('video').play();

    // // In browsers that don't yet support this functionality,
    // // playPromise won't be defined.
    // if (playPromise !== undefined) {
    //     playPromise
    //         .then(function () {
    //             // Automatic playback started!
    //         })
    //         .catch(function (error) {
    //             // Automatic playback failed.
    //             // Show a UI element to let the user manually start playback.
    //         });
    // }

    return (
        <>
            <div
                style={{
                    position: 'relative',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    border: `1px solid ${PALE_WHITE_1}`,
                    color: 'rgba(255,255,255,0.8)',
                    borderRadius: 12,
                    padding: 8,
                    ...style
                }}
            >
                <div>
                    {/* <audio controls>
                        <source src={url} type="audio/aac" />
                    </audio> */}
                    <ReactPlayer
                        ref={playerRef}
                        width="0"
                        height="0"
                        url={playbackUrl}
                        playing={playing}
                        loop={loop}
                        playbackRate={playbackRate}
                        volume={0.8}
                        muted={muted}
                        onReady={() => {
                            syncDurationFromInternalPlayer();
                        }}
                        onStart={() => console.log('onStart')}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        onBuffer={() => console.log('onBuffer')}
                        onSeek={e => console.log('onSeek', e)}
                        onEnded={handleEnded}
                        onError={handlePlaybackError}
                        onProgress={handleProgress}
                        onDuration={handleDuration}
                    />
                </div>
                <div className="audio-preview-row">
                    <div className="audio-preview-play-button" onClick={handlePlayPause}>
                        {!playing ? (
                            <FaPlay style={{ height: 16, width: 16, color: WHITE, marginLeft: 2 }} />
                        ) : (
                            <IoIosPause style={{ height: 20, width: 20, color: WHITE }} />
                        )}
                    </div>
                    <div className="audio-preview-main">
                        {hasStartedPlayback ? (
                            <input
                                className="audio-shared-scrubber audio-preview-scrubber"
                                type="range"
                                min={0}
                                max={0.999999}
                                step="any"
                                value={played}
                                onMouseDown={handleSeekMouseDown}
                                onChange={handleSeekChange}
                                onMouseUp={handleSeekMouseUp}
                            />
                        ) : (
                            <div className="audio-preview-title">{title}</div>
                        )}
                        <div className={`audio-preview-meta-row${hasStartedPlayback ? ' audio-preview-meta-row--with-scrubber' : ''}`}>
                            <div className="audio-preview-time-group">
                                <span>
                                    {hasStartedPlayback ? (
                                        <>
                                            <Duration seconds={duration * played} /> / <Duration seconds={duration} />
                                        </>
                                    ) : (
                                        <Duration seconds={duration} />
                                    )}
                                </span>
                                {hasStartedPlayback ? (
                                    <span className="audio-speed-slot">
                                        <button
                                            type="button"
                                            className="audio-shared-pill-button"
                                            onClick={handleCyclePlaybackRate}
                                            title={lnT('messages:playbackSpeed', { value1: String(formatPlaybackRate(playbackRate)) })}
                                        >
                                            {formatPlaybackRate(playbackRate)}
                                        </button>
                                    </span>
                                ) : null}
                            </div>
                            <div className="audio-preview-meta-actions">
                                {playing ? (
                                    <button
                                        type="button"
                                        onClick={handleToggleMuted}
                                        className={`audio-shared-pill-button${muted ? ' audio-shared-pill-button--active' : ''}`}
                                        title={muted ? lnT('messages:unmuteAudio') : lnT('messages:muteAudio')}
                                    >
                                        {muted ? <IoIosVolumeOff size={14} /> : <IoIosVolumeHigh size={14} />}
                                    </button>
                                ) : null}
                                {playing ? (
                                    <button
                                        type="button"
                                        onClick={handleToggleLoop}
                                        className={`audio-shared-pill-button${loop ? ' audio-shared-pill-button--active' : ''}`}
                                        title={loop ? lnT('messages:disableLoop') : lnT('messages:enableLoop')}
                                    >
                                        <IoIosRepeat size={14} />
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="audio-preview-side-actions">
                        {showTranscriptionAction ? (
                            <React.Fragment>
                        <button
                                    ref={transcriptionMenuTriggerRef}
                                    type="button"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleOpenTranscriptionOutputMenu();
                                    }}
                                    className="audio-transcribe-pill-button"
                                    style={{
                                        cursor: isTranscribing ? 'not-allowed' : 'pointer',
                                        opacity: isTranscribing ? 0.5 : 1
                                    }}
                                    title={
                                        isTranscribing
                                            ? lnT('messages:transcribing_4e9a9366')
                                            : (requiresUpgradeForTranscription || requiresTeamUpgradeForTranscription)
                                                ? upgradeTooltip
                                                : lnT('messages:chooseTranscriptionFormat')
                                    }
                                >
                                    {isTranscribing ? (
                                        <div className="audio-transcribe-pill-spinner" />
                                    ) : (
                                        <React.Fragment>
                                            <FiArrowRight size={12} color="currentColor" aria-hidden="true" />
                                            <span className="audio-transcribe-pill-label">{lnT('messages:a')}</span>
                                        </React.Fragment>
                                    )}
                                </button>
                            </React.Fragment>
                        ) : null}
                        {attachmentActions}
                    </div>
                </div>
                {renderTranscriptionMenuPortal()}

                {transcriptionError ? (
                    <div className="audio-transcription-status audio-transcription-status--error">
                        <span>{transcriptionError}</span>
                    </div>
                ) : null}
                {!transcriptionError && (serverTranscriptionStatus || transcriptionStatus) ? (
                    <div className={`audio-transcription-status audio-transcription-status--${transcriptionStatusTone}`}>
                        {transcriptionStatusTone === 'progress' ? <div className="audio-transcription-status-spinner" /> : null}
                        <span>{serverTranscriptionStatus || transcriptionStatus}</span>
                    </div>
                ) : null}
                {isTranscribing ? (
                    <div className="audio-transcription-status audio-transcription-status--progress">
                        <div className="audio-transcription-status-spinner" />
                        <span>{lnT('messages:transcriptionRunningInTheBackground')}</span>
                    </div>
                ) : null}
            </div>
            <Modal
                isOpen={showTranscriptionModal}
                onRequestClose={handleCloseTranscriptionModal}
                style={transcriptionModalStyles}
                contentLabel={lnT('messages:transcriptionSettings')}
            >
                <div className="audio-transcription-modal">
                    <div className="audio-transcription-modal__header">
                        <div>
                            <div className="audio-transcription-modal__title">{lnT('messages:transcribeAudio')}</div>
                            <p className="audio-transcription-modal__description">{lnT('messages:chooseTheTranscriptionModeAndSourceLanguage')}</p>
                        </div>
                    </div>

                    <div className="audio-transcription-modal__content">
                        <div className="audio-transcription-modal__field">
                            <label className="audio-transcription-modal__label">{lnT('messages:transcriptionMode')}</label>
                            <select
                                className="audio-transcription-modal__select"
                                value={selectedProvider}
                                onChange={e => {
                                    setSelectedProvider(e.target.value);
                                    if (e.target.value === 'groq') {
                                        setSelectedGroqModel('whisper_large_v3_turbo');
                                    }
                                }}
                            >
                                <option value="openai">{lnT('messages:precise')}</option>
                                <option value="groq">{lnT('messages:fast')}</option>
                            </select>
                            <div className="audio-transcription-modal__helper">
                                {selectedProvider === 'openai'
                                    ? lnT('messages:preciseGivesTheBestAccuracyAndBroadest')
                                    : lnT('messages:fastIsQuickerAndCheaperWithAn')}
                            </div>
                        </div>

                        {selectedProvider === 'groq' ? (
                            <div className="audio-transcription-modal__field">
                                <label className="audio-transcription-modal__label">{lnT('messages:speedQuality')}</label>
                                <select
                                    className="audio-transcription-modal__select"
                                    value={selectedGroqModel}
                                    onChange={e => setSelectedGroqModel(e.target.value)}
                                >
                                    <option value="whisper_large_v3_turbo">{lnT('messages:standardFastFasterLowerCost')}</option>
                                    <option value="whisper_large_v3">{lnT('messages:accurateFastHigherAccuracy')}</option>
                                </select>
                                <div className="audio-transcription-modal__helper">{lnT('messages:standardFastPrioritizesSpeedAccurateFastIs')}</div>
                            </div>
                        ) : null}

                        <div className="audio-transcription-modal__field">
                            <label className="audio-transcription-modal__label">{lnT('messages:sourceLanguage')}</label>
                            <button
                                type="button"
                                className="audio-transcription-modal__language-trigger"
                                onClick={() => setShowLanguagePicker(previous => !previous)}
                            >
                                <span>
                                    <span className="audio-transcription-modal__language-trigger-title">
                                        {selectedLanguageOption ? `${selectedLanguageOption.flag} ${selectedLanguageOption.label}` : lnT('messages:autoDetect')}
                                    </span>
                                    <span className="audio-transcription-modal__language-trigger-meta">
                                        {selectedLanguageOption?.value
                                            ? lnT('messages:selectedSourceLanguage', { value1: String(selectedLanguageOption.value) })
                                            : lnT('messages:detectAutomatically')}
                                    </span>
                                </span>
                                <span className="audio-transcription-modal__chevron">
                                    {showLanguagePicker ? '▴' : '▾'}
                                </span>
                            </button>

                            {showLanguagePicker ? (
                                <div className="audio-transcription-modal__language-picker">
                                    <input
                                        type="text"
                                        className="audio-transcription-modal__search"
                                        value={languageSearchQuery}
                                        onChange={e => setLanguageSearchQuery(e.target.value)}
                                        placeholder={lnT('messages:searchLanguageOrCode')}
                                        autoCapitalize="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                    />
                                    <div className="audio-transcription-modal__language-list">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                languageTouched.current = true;
                                                setSelectedLanguage('');
                                                setShowLanguagePicker(false);
                                            }}
                                            className={`audio-transcription-modal__language-option${
                                                !selectedLanguage ? ' audio-transcription-modal__language-option--selected' : ''
                                            }`}
                                        >
                                            <span>{lnT('messages:autoDetect')}</span>
                                            <span className="audio-transcription-modal__language-code">{lnT('messages:auto')}</span>
                                        </button>
                                        {filteredLanguageOptions.length > 0 ? (
                                            filteredLanguageOptions.map(option => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => {
                                                        languageTouched.current = true;
                                                        setSelectedLanguage(option.value);
                                                        setShowLanguagePicker(false);
                                                    }}
                                                    className={`audio-transcription-modal__language-option${
                                                        selectedLanguage === option.value
                                                            ? ' audio-transcription-modal__language-option--selected'
                                                            : ''
                                                    }`}
                                                >
                                                    <span>{option.flag} {option.label}</span>
                                                    <span className="audio-transcription-modal__language-code">
                                                        {option.value}
                                                    </span>
                                                </button>
                                            ))
                                        ) : (
                                            <div className="audio-transcription-modal__empty-state">{lnT('messages:noLanguagesMatchedThatSearch')}</div>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="audio-transcription-modal__actions">
                        <button
                            type="button"
                            onClick={handleCloseTranscriptionModal}
                            className="audio-transcription-modal__secondary-button"
                        >{lnT('common:cancel')}</button>
                        <button
                            type="button"
                            onClick={handleSubmitTranscription}
                            className="audio-transcription-modal__primary-button"
                        >{lnT('messages:transcribe')}</button>
                    </div>
                </div>
            </Modal>
            <CreditsRequiredModal
                isOpen={showCreditsRequiredModal}
                onRequestClose={() => setShowCreditsRequiredModal(false)}
                onCheckout={() => {
                    setShowCreditsRequiredModal(false);
                    openSettingsModal(SETTINGS_MODAL_TAB.UPGRADES);
                }}
            />
        </>
    );
}

export default AudioPlayer;
